import { Fragment, useEffect, useState, useMemo } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import SignatureModal from "../Common/SignatureModal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["clean"],
  ],
};

const customSelectStyles = {
 control: (base) => ({
 ...base,
 height: "3.5rem",
 minHeight: "3.5rem",
 display: "flex",
 alignItems: "center",
 borderRadius: "1rem",
 borderColor: "#d7dae3",
 boxShadow: "none",
 "&:hover": {
 borderColor: "#d7dae3",
 },
 }),
 valueContainer: (base) => ({
 ...base,
 height: "100%",
 padding: "0 1.25rem",
 display: "flex",
 alignItems: "center",
 }),
 input: (base) => ({
 ...base,
 margin: "0",
 padding: "0",
 }),
 singleValue: (base) => ({
 ...base,
 margin: 0,
 lineHeight: "1",
 }),
 indicatorsContainer: (base) => ({
 ...base,
 height: "100%",
 }),
 dropdownIndicator: (base) => ({
 ...base,
 paddingTop: "0.25rem",
 paddingBottom: "0.25rem",
 }),
 clearIndicator: (base) => ({
 ...base,
 paddingTop: "0.25rem",
 paddingBottom: "0.25rem",
 }),
 placeholder: (base) => ({
 ...base,
 color: "#6e6e6e",
 }),
};

const compactControlHeight = 38;

const DprAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
  const projectFromUrl = searchParams.get("project") || "";

 // ==========================================
 // STATES
 // ==========================================


 const [workOrders, setWorkOrders] = useState([]);

  const filteredWorkOrders = useMemo(() => {
    if (!projectFromUrl) return workOrders;
    return workOrders.filter((wo) => {
      const pid = wo.project?._id || wo.project;
      return String(pid) === String(projectFromUrl);
    });
  }, [workOrders, projectFromUrl]);

 const [boqItems, setBoqItems] = useState([]);
 const [activityMasters, setActivityMasters] = useState([]);
 const [units, setUnits] = useState([]);
 const [dprUsageMap, setDprUsageMap] = useState({});
 const [loading, setLoading] = useState(false);
 const [tasks, setTasks] = useState([]);
 const [selectedTask, setSelectedTask] = useState(null);
 const [photos, setPhotos] = useState([]);
 const [previousOpenIssues, setPreviousOpenIssues] = useState([]);
 const [previousIssueUpdates, setPreviousIssueUpdates] = useState({});
 const [loadingPrevIssues, setLoadingPrevIssues] = useState(false);
 const [showSignatureModal, setShowSignatureModal] = useState(false);
 const [manualItem, setManualItem] = useState({
 activity: "",
 quantity: "",
 unit: "",
 location: "",
 wastage: 0,
 status: "Pending",
 });

 const [form, setForm] = useState({
 workOrder: "",
 date: new Date().toISOString().split("T")[0],
 activitiesCompleted: [],
 manpower: {
 supervisors: 0,
 engineers: 0,
 skilled: 0,
 unskilled: 0,
 total: 0,
 },
 issuesRaised: false,
 issueDetails: [],
 nextDayPlan: "",
 weather: "",
 weatherImpact: "No",
 status: "Draft",
 });

 const [activityItem, setActivityItem] = useState({
 boqItem: "",
 activityMasterId: "",
 activity: "",
 description: "",
 location: "",
 drawingRef: "",
 todayQty: 0,
 previousQtyToDate: 0,
 totalQtyToDate: 0,
 quantityCompleted: "",
 wastageQty: "",
 status: "Pending",
 });

 const [issueItem, setIssueItem] = useState({
 issue: "",
 description: "",
 severity: "Low",
 status: "Open",
 });

 // ==========================================
 // FETCH WORK ORDERS
 // ==========================================
 useEffect(() => {
 fetchWorkOrders();
 fetchActivityMasters();
 fetchUnits();

 if (workOrderFromUrl) {
 setForm((prev) => ({
 ...prev,
 workOrder: workOrderFromUrl,
 }));
 }
 }, []);

 const fetchActivityMasters = async () => {
 try {
 const res = await api.get("/activity-masters");
 if (res.data?.success) {
 setActivityMasters(res.data.data || []);
 }
 } catch (error) {
 swal("Error", "Failed to fetch activity master", "error");
 }
 };

 const fetchUnits = async () => {
 try {
 const res = await api.get("/boq-unit-types");
 if (res.data?.success) {
 setUnits(res.data.data || []);
 }
 } catch (error) {
 swal("Error", "Failed to fetch unit master", "error");
 }
 };

 const fetchDprUsage = async (workOrderId) => {
 try {
 const res = await api.get(`/dpr/usage/${workOrderId}`);
 if (res.data?.success) {
 setDprUsageMap(res.data.map || {});
 }
 } catch (error) {
 setDprUsageMap({});
 }
 };

 const fetchPreviousOpenIssues = async (workOrderId) => {
 if (!workOrderId) {
 setPreviousOpenIssues([]);
 setPreviousIssueUpdates({});
 return;
 }
 try {
 setLoadingPrevIssues(true);
 const res = await api.get(`/dpr/open-issues/${workOrderId}`);
 if (res.data?.success) {
 const issues = res.data.data || [];
 setPreviousOpenIssues(issues);
 const initialUpdates = {};
 issues.forEach((iss) => {
 const key = iss.groupKey || iss.issueId || `${iss.dprId}_${iss.issueIndex}`;
 initialUpdates[key] = {
 groupKey: iss.groupKey,
 items: iss.items || [
 {
 dprId: iss.dprId,
 issueId: iss.issueId,
 issueIndex: iss.issueIndex,
 },
 ],
 originalStatus: iss.status || "Open",
 status: iss.status || "Open",
 resolutionRemarks: iss.resolutionRemarks || "",
 };
 });
 setPreviousIssueUpdates(initialUpdates);
 } else {
 setPreviousOpenIssues([]);
 setPreviousIssueUpdates({});
 }
 } catch (error) {
 setPreviousOpenIssues([]);
 setPreviousIssueUpdates({});
 } finally {
 setLoadingPrevIssues(false);
 }
 };

 const fetchWorkOrders = async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data?.success) {
 const list = res.data.data;
 setWorkOrders(list);

 // Fix: If workOrder was pre-filled from URL, trigger BOQ fetch immediately
 if (workOrderFromUrl) {
 const preSelected = list.find((w) => w._id === workOrderFromUrl);
 if (preSelected) {
 fetchBOQ(workOrderFromUrl);
 fetchDprUsage(workOrderFromUrl);
 fetchPreviousOpenIssues(workOrderFromUrl);
 if (preSelected.project?._id) {
 fetchTasksByProject(preSelected.project._id);
 }
 }
 }
 } else {
 setWorkOrders([]);
 }
 } catch (error) {
 swal("Error", "Failed to fetch work orders", "error");
 }
 };
 const fetchTasksByProject = async (projectId) => {
 try {
 const res = await api.get(`/tasks/by-project/${projectId}`);
 if (res.data.success) {
 setTasks(res.data.data);
 }
 } catch {
 swal("Error", "Failed to fetch tasks", "error");
 }
 };

 const fetchBOQ = async (workOrderId) => {
 try {
 const res = await api.get(`/work-orders/${workOrderId}`);
 if (res.data.success) {
 setBoqItems(res.data.data.boqItems || []);
 }
 } catch {
 swal("Error", "Failed to fetch BOQ items", "error");
 }
 };

 // ==========================================
 // HANDLERS
 // ==========================================
 const handleChange = (e) => {
 setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
 };

 const handleManpowerChange = (e) => {
 const { name, value } = e.target;
 const val = Number(value || 0);

 setForm((prev) => {
 const manpower = { ...prev.manpower, [name]: val };
 manpower.total =
 manpower.supervisors +
 manpower.engineers +
 manpower.skilled +
 manpower.unskilled;

 return { ...prev, manpower };
 });
 };
 useEffect(() => {
 if (!form.workOrder) {
 setPreviousOpenIssues([]);
 setPreviousIssueUpdates({});
 return;
 }

 const selectedWO = workOrders.find((w) => w._id === form.workOrder);

 if (!selectedWO) return;

 // BOQ & usage from WorkOrder
 fetchBOQ(form.workOrder);
 fetchDprUsage(form.workOrder);
 fetchPreviousOpenIssues(form.workOrder);

 // Tasks from Project (derived)
 if (selectedWO.project?._id) {
 fetchTasksByProject(selectedWO.project._id);
 }
 }, [form.workOrder]);

 const parseActivityNum = (value) => {
 const num = Number(value);
 return Number.isFinite(num) ? num : 0;
 };

 const computeActivityTodayQty = (item) => {
 const doneQty = parseActivityNum(item.quantityCompleted);
 return Number(doneQty.toFixed(3));
 };

 const syncComputedActivity = (next) => {
 const todayQty = computeActivityTodayQty(next);
 const previousQtyToDate = parseActivityNum(next.previousQtyToDate);
 const totalQtyToDate = Number((previousQtyToDate + todayQty).toFixed(3));
 const shouldAutoFillDone =
 next.quantityCompleted === "" ||
 next.quantityCompleted === null ||
 parseActivityNum(next.quantityCompleted) === 0;

 return {
 ...next,
 todayQty,
 totalQtyToDate,
 quantityCompleted: shouldAutoFillDone ? todayQty : next.quantityCompleted,
 };
 };

 const handleActivityChange = (e) => {
 const { name, value } = e.target;
 setActivityItem((prev) => {
 const next = { ...prev, [name]: value };
 if (["quantityCompleted", "previousQtyToDate"].includes(name)) {
 const selectedBOQ = boqItems.find((b) => b._id === next.boqItem);
 const boqQty = parseActivityNum(selectedBOQ?.quantity);
 const prevQty = parseActivityNum(next.previousQtyToDate);
 const wastageQty = parseActivityNum(next.wastageQty);
 const maxDone = Math.max(0, boqQty - prevQty - wastageQty);
 const doneQty = Math.min(parseActivityNum(next.quantityCompleted), maxDone);
 if (name === "quantityCompleted" && parseActivityNum(next.quantityCompleted) > maxDone) {
 swal("Warning", `Done quantity cannot exceed ${maxDone} (remaining BOQ)`, "warning");
 }
 next.quantityCompleted = doneQty;
 return syncComputedActivity(next);
 }
 return next;
 });
 };
 const addActivityItem = () => {
 if (!activityItem.boqItem)
 return swal("Warning", "Select a BOQ item", "warning");

 if (!activityItem.activityMasterId)
 return swal("Warning", "Select an activity", "warning");

 if (!activityItem.location?.trim())
 return swal("Warning", "Location / Chainage is required", "warning");

 if (!activityItem.activity.trim())
 return swal("Warning", "Enter activity name", "warning");

 if (activityItem.quantityCompleted <= 0)
 return swal("Warning", "Enter valid completed quantity", "warning");

 // NEW — WASTAGE CHECK
 if (activityItem.wastageQty < 0)
 return swal("Warning", "Wastage cannot be negative", "warning");

 const selectedBOQ = boqItems.find((b) => b._id === activityItem.boqItem);
 const selectedActivity = activityMasters.find(
 (a) => a._id === activityItem.activityMasterId,
 );

 // OPTIONAL — Must enter wastage
 if (activityItem.wastageQty === "" || activityItem.wastageQty === null) {
 return swal("Warning", "Enter wastage quantity (0 if none)", "warning");
 }

 // SAFETY — Total cannot exceed BOQ quantity
 const totalUsed =
 Number(activityItem.quantityCompleted) + Number(activityItem.wastageQty);

 if (selectedBOQ && totalUsed > selectedBOQ.quantity) {
 return swal(
 "Warning",
 "Completed + Wastage cannot exceed BOQ quantity",
 "warning",
 );
 }

 setForm((prev) => ({
 ...prev,
 activitiesCompleted: [
 ...prev.activitiesCompleted,
 {
 ...activityItem,
 activityMasterId: selectedActivity?._id || activityItem.activityMasterId,
 activity: selectedActivity?.name || activityItem.activity,
 boqName: `[WLI-${selectedBOQ?.wlino || "-"}] ${selectedBOQ?.itemName}`,
 unit: selectedBOQ?.unit,
 boqQty: selectedBOQ?.quantity,
 quantity: selectedBOQ?.quantity,
 todayQty: activityItem.todayQty,
 previousQtyToDate: activityItem.previousQtyToDate,
 totalQtyToDate: activityItem.totalQtyToDate,
 location: activityItem.location,
 drawingRef: activityItem.drawingRef,
 },
 ],
 }));

 swal("Success", "Activity added", "success");

 setActivityItem({
 boqItem: "",
 activityMasterId: "",
 activity: "",
 description: "",
 location: "",
 drawingRef: "",
 todayQty: 0,
 previousQtyToDate: 0,
 totalQtyToDate: 0,
 quantityCompleted: 0,
 wastageQty: 0,
 status: "Pending",
 });
 };

 const removeActivityItem = (index) => {
 setForm((prev) => ({
 ...prev,
 activitiesCompleted: prev.activitiesCompleted.filter(
 (_, i) => i !== index,
 ),
 }));
 swal("Success", "Activity removed", "success");
 };

 const handleIssueChange = (e) => {
 setIssueItem((prev) => ({ ...prev, [e.target.name]: e.target.value }));
 };

 const addIssueItem = (e) => {
  if (e) {
   e.preventDefault();
   e.stopPropagation();
  }
  if (!issueItem.issue.trim())
   return swal("Warning", "Enter issue", "warning");

  setForm((prev) => ({
   ...prev,
   issueDetails: [...prev.issueDetails, { ...issueItem }],
   issuesRaised: true,
  }));

  swal("Success", "Issue added", "success");

  setIssueItem({
   issue: "",
   description: "",
   severity: "Low",
   status: "Open",
  });
 };
 // ADD MANUAL ACTIVITY
 const addManualActivity = () => {
 if (!manualItem.activity?.trim())
 return swal("Warning", "Enter manual activity", "warning");
 if (!manualItem.unit?.trim())
 return swal("Warning", "Select unit", "warning");

 setForm((prev) => ({
 ...prev,
 activitiesCompleted: [
 ...prev.activitiesCompleted,
 {
 boqName: "Manual Activity",
 boqQty: "-",
 activity: manualItem.activity,
 location: manualItem.location,
 quantityCompleted: manualItem.quantity,
 wastageQty: manualItem.wastage,
 unit: manualItem.unit,
 status: manualItem.status,
 },
 ],
 }));

 setManualItem({
 activity: "",
 quantity: "",
 unit: "",
 location: "",
 wastage: 0,
 status: "Pending",
 });
 };

 const removeIssueItem = (index) => {
 const updated = form.issueDetails.filter((_, i) => i !== index);

 setForm((prev) => ({
 ...prev,
 issueDetails: updated,
 issuesRaised: updated.length > 0,
 }));

 swal("Success", "Issue removed", "success");
 };
 const handlePhotoChange = (e) => {
 const files = Array.from(e.target.files);

 const formatted = files.map((file) => ({
 file,
 description: "",
 preview: URL.createObjectURL(file), // Generate preview URL
 }));

 setPhotos((prev) => [...prev, ...formatted]);
 e.target.value = ""; // Reset input so same file can be selected again
 };

 const removePhoto = (index) => {
 setPhotos((prev) => {
 const updated = [...prev];
 // Revoke the object URL to avoid memory leaks
 if (updated[index].preview) {
 URL.revokeObjectURL(updated[index].preview);
 }
 updated.splice(index, 1);
 return updated;
 });
 };
 // ==========================================
 // SUBMIT FORM
 // ==========================================
 const handleSubmit = (e) => {
  e.preventDefault();

  if (!form.workOrder) return swal("Warning", "Select work order", "warning");
  if (!form.date) return swal("Warning", "Select date", "warning");

  setShowSignatureModal(true);
 };

 const handleSignatureSave = async (signatureFile, stampFile, remarks) => {
  setShowSignatureModal(false);

  const formData = new FormData();

  // Add form fields
  formData.append("workOrder", form.workOrder);
  formData.append("date", form.date);
  formData.append("nextDayPlan", form.nextDayPlan);
  formData.append("weather", form.weather);
  formData.append("weatherImpact", form.weatherImpact || "No");
  formData.append("status", form.status);

  formData.append("manpower", JSON.stringify(form.manpower));
  const activitiesToSubmit = [...(form.activitiesCompleted || [])];
  if (manualItem.activity && manualItem.activity.trim()) {
    activitiesToSubmit.push({
      boqName: "Manual Activity",
      boqQty: "-",
      activity: manualItem.activity.trim(),
      location: manualItem.location || "",
      quantityCompleted: Number(manualItem.quantity) || 0,
      wastageQty: Number(manualItem.wastage) || 0,
      unit: manualItem.unit || "Nos",
      status: manualItem.status || "Pending",
    });
  }
  formData.append(
    "activitiesCompleted",
    JSON.stringify(activitiesToSubmit)
  );
  const issuesToSubmit = [...form.issueDetails];
  if (issueItem.issue && issueItem.issue.trim()) {
    issuesToSubmit.push({ ...issueItem });
  }
  formData.append("issueDetails", JSON.stringify(issuesToSubmit));
  formData.append("issuesRaised", String(issuesToSubmit.length > 0));

  const updatesToSubmit = Object.values(previousIssueUpdates).filter(
  (u) => u.status !== u.originalStatus || (u.resolutionRemarks && u.resolutionRemarks.trim())
  );
  if (updatesToSubmit.length > 0) {
  formData.append("previousIssueUpdates", JSON.stringify(updatesToSubmit));
  }

  if (selectedTask?._id) formData.append("taskId", selectedTask._id);
  if (selectedTask?.assignedEmployeeId) formData.append("employeeId", selectedTask.assignedEmployeeId);

  // ADD PHOTOS
  photos.forEach((photo, index) => {
  formData.append("photos", photo.file);
  formData.append(`photoDescriptions[${index}]`, photo.description);
  });

  // ADD SIGNATURE & REMARKS
  if (signatureFile) {
  formData.append("signature", signatureFile);
  }
  if (remarks) {
  formData.append("remarks", remarks);
  }

  setLoading(true);

  try {
  const res = await api.post("/dpr", formData, {
  headers: { "Content-Type": "multipart/form-data" },
  });

  if (res.data?.success) {
  swal("Success", "DPR created successfully!", "success");

  navigate(
  workOrderFromUrl ? `/dpr?workOrder=${workOrderFromUrl}` : "/dpr"
  );
  } else {
  swal("Error", res.data.message || "Failed to create DPR", "error");
  }
  } catch (error) {
  swal("Error", error.response?.data?.message || "Server error", "error");
  } finally {
  setLoading(false);
  }
 };
 // ==========================================
 // DERIVED SELECTED BOQ ( ADD THIS)
 // ==========================================
 const selectedBOQ = boqItems.find((b) => b._id === activityItem.boqItem);
 const issueStats = form.issueDetails.reduce(
 (acc, issue) => {
 const severity = String(issue?.severity || "").toLowerCase();
 const status = String(issue?.status || "").toLowerCase();
 acc.total += 1;
 if (status === "open") acc.open += 1;
 if (status === "in progress") acc.inProgress += 1;
 if (status === "resolved") acc.resolved += 1;
 if (severity === "critical") acc.critical += 1;
 return acc;
 },
 { total: 0, open: 0, inProgress: 0, resolved: 0, critical: 0 },
 );
 const latestIssue = form.issueDetails[form.issueDetails.length - 1];
 // ==========================================
 // RENDER UI
 // ==========================================
 return (
 <Fragment>
 <PageTitle activeMenu="Add DPR" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Add DPR</Card.Title>
 <Button
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/dpr?workOrder=${workOrderFromUrl}`
 : "/dpr",
 )
 }
 >
 Back
 </Button>
 </Card.Header>

 <Card.Body>
 <form onSubmit={handleSubmit}>
 {/* DPR Number Auto-Generated */}
 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>DPR Number</Form.Label>
 <Form.Control
 type="text"
 placeholder="Auto-generated"
 readOnly
 />
 </Form.Group>
 </Col>

 {/* Project */}
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Work Order *</Form.Label>
 <Form.Select
 className="form-control"
 name="workOrder"
 value={form.workOrder}
 onChange={handleChange}
 >
 <option value="">Select Work Order</option>
 {filteredWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} - {wo.project?.projectName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 {/* Date */}
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Date *</Form.Label>
 <Form.Control
 type="date"
 name="date"
 value={form.date}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 </Row>

 {/* Task */}
 {/* Task */}
 <Row>
 <Col md={4}>
 <Form.Group className="mb-3">
 <Form.Label>Task (Project Based)</Form.Label>
 <Form.Select
 className="form-control"
 value={selectedTask?._id || ""}
 onChange={(e) => {
 const t = tasks.find(
 (task) => task._id === e.target.value,
 );
 setSelectedTask(t || null);
 }}
 >
 <option value="">Select Task</option>
 {tasks.map((t) => (
 <option key={t._id} value={t._id}>
 {t.title} ({t.status})
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 {selectedTask && (
 <Card
 className="p-2 mb-3 dpr-task-preview-card"
 >
 <small>
 <strong>Assigned To:</strong>{" "}
 {selectedTask.employeeName || "Unassigned"}
 </small>
 <br />
 <small>
 <strong>Task Description:</strong>{" "}
 {selectedTask.description || "N/A"}
 </small>
 <br />
 <small>
 <strong>Deadline:</strong>{" "}
 {selectedTask.plannedEndDate
 ? new Date(
 selectedTask.plannedEndDate,
 ).toLocaleDateString()
 : "N/A"}
 </small>
 <br />
 <small>
 <strong>Estimated Hours:</strong>{" "}
 {selectedTask.estimatedHours}
 </small>
 <br />
 <small>
 <strong>Status:</strong> {selectedTask.status}
 </small>
 </Card>
 )}
 </Row>

 {/* MANPOWER */}
 <Row>
 {["supervisors", "engineers", "skilled", "unskilled"].map(
 (role) => (
 <Col md={3} key={role}>
 <Form.Group className="mb-3">
 <Form.Label>
 {role.charAt(0).toUpperCase() + role.slice(1)}
 </Form.Label>
 <Form.Control
 type="number"
 name={role}
 value={form.manpower[role]}
 onChange={handleManpowerChange}
 />
 </Form.Group>
 </Col>
 ),
 )}
 </Row>

 <Row>
 <Col md={3}>
 <Form.Group className="mb-3">
 <Form.Label>Total Manpower</Form.Label>
 <Form.Control
 type="number"
 value={form.manpower.total}
 readOnly
 />
 </Form.Group>
 </Col>
 </Row>

 {/* ACTIVITIES */}
 <div className="mb-4">
 <h5 className="fw-bold mb-3">Activities Completed</h5>

 {/* ================= BOQ ACTIVITY ================= */}

 <Row className="g-3 align-items-end mb-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label>BOQ Item</Form.Label>

 <Select
 styles={customSelectStyles}
 classNamePrefix="boq-select"
 placeholder="Search BOQ..."
 options={boqItems.map((b) => ({
 value: b._id,
 label: `[WLI-${b.wlino || "-"}] ${b.itemName} (${b.unit})`,
 data: b,
 }))}
 value={
 activityItem.boqItem
 ? {
 value: activityItem.boqItem,
 label: selectedBOQ
 ? `[WLI-${selectedBOQ.wlino || "-"}] ${selectedBOQ.itemName} (${selectedBOQ.unit})`
 : "",
 }
 : null
 }
 onChange={(opt) =>
 setActivityItem((prev) => {
 const next = {
 ...prev,
 boqItem: opt?.value || "",
 unit: boqItems.find((b) => b._id === opt?.value)?.unit || "",
 previousQtyToDate: dprUsageMap[opt?.value] || 0,
 };
 return syncComputedActivity(next);
 })
 }
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>BOQ Quantity</Form.Label>

 <Form.Control
 type="text"
 value={
 selectedBOQ
 ? `${selectedBOQ.quantity} ${selectedBOQ.unit}`
 : ""
 }
 readOnly
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Unit</Form.Label>
 <Form.Control
 type="text"
 value={selectedBOQ?.unit || ""}
 readOnly
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Activity</Form.Label>

 <Form.Select
 name="activityMasterId"
 value={activityItem.activityMasterId}
 onChange={(e) => {
 const selected = activityMasters.find(
 (item) => item._id === e.target.value,
 );
 setActivityItem((prev) => ({
 ...prev,
 activityMasterId: e.target.value,
 activity: selected?.name || "",
 }));
 }}
 >
 <option value="">Select Activity</option>
 {activityMasters.map((activity) => (
 <option key={activity._id} value={activity._id}>
 {activity.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Done</Form.Label>

 <Form.Control
 type="number"
 name="quantityCompleted"
 value={activityItem.quantityCompleted}
 onChange={handleActivityChange}
 min={0}
 max={Math.max(
 0,
 parseActivityNum(selectedBOQ?.quantity) -
 parseActivityNum(activityItem.previousQtyToDate) -
 parseActivityNum(activityItem.wastageQty),
 )}
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Location / Chainage</Form.Label>
 <Form.Control
 type="text"
 name="location"
 value={activityItem.location}
 onChange={handleActivityChange}
 placeholder="Block A - Floor 3"
 maxLength={80}
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Drawing Ref.</Form.Label>
 <Form.Control
 type="text"
 name="drawingRef"
 value={activityItem.drawingRef}
 onChange={handleActivityChange}
 placeholder="DWG-01"
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Today Qty</Form.Label>
 <Form.Control
 type="number"
 value={activityItem.todayQty}
 readOnly
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Prev. Qty</Form.Label>
 <Form.Control
 type="number"
 name="previousQtyToDate"
 value={activityItem.previousQtyToDate}
 onChange={handleActivityChange}
 readOnly
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Total Qty</Form.Label>
 <Form.Control
 type="number"
 value={activityItem.totalQtyToDate}
 readOnly
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Waste</Form.Label>

 <Form.Control
 type="number"
 name="wastageQty"
 value={activityItem.wastageQty}
 onChange={handleActivityChange}
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Status</Form.Label>

 <Form.Select
 className="form-control"
 name="status"
 value={activityItem.status}
 onChange={handleActivityChange}
 >
 <option value="Pending">Pending</option>
 <option value="In Progress">In Progress</option>
 <option value="Completed">Completed</option>
 <option value="QC Hold">QC Hold</option>
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={1} >
 <Button
 variant="primary"
 className="w-100 h-100"
 style={{
 height: `${compactControlHeight}px`,
 minHeight: `${compactControlHeight}px`,
 paddingTop: 0,
 paddingBottom: 0,
 }}
 onClick={addActivityItem}
 >
 Add
 </Button>
 </Col>
 </Row>

 {/* ================= MANUAL ACTIVITY ================= */}

 <h6 className="text-warning mb-3">
 Manual Activity (Without BOQ)
 </h6>

 <Row className="g-3 align-items-end mb-3">
 <Col md={2}>
 <Form.Group>
 <Form.Label>Activity</Form.Label>

 <Form.Control
 type="text"
 value={manualItem.activity}
 placeholder="Manual Activity"
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 activity: e.target.value,
 }))
 }
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Quantity</Form.Label>

 <Form.Control
 type="number"
 value={manualItem.quantity}
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 quantity: e.target.value,
 }))
 }
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Unit</Form.Label>

 <Form.Select
 value={manualItem.unit}
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 unit: e.target.value,
 }))
 }
 >
 <option value="">Select Unit</option>
 {units.map((unit) => (
 <option key={unit._id} value={unit.symbol || unit.name}>
 {unit.name}
 {unit.symbol ? ` (${unit.symbol})` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Location</Form.Label>

 <Form.Control
 type="text"
 value={manualItem.location}
 placeholder="Block A - Floor 3"
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 location: e.target.value,
 }))
 }
 />
 </Form.Group>
 </Col>

 <Col md={1}>
 <Form.Group>
 <Form.Label>Waste</Form.Label>

 <Form.Control
 type="number"
 value={manualItem.wastage}
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 wastage: e.target.value,
 }))
 }
 />
 </Form.Group>
 </Col>

 <Col md={2}>
 <Form.Group>
 <Form.Label>Status</Form.Label>

 <Form.Select
 className="form-control"
 value={manualItem.status}
 onChange={(e) =>
 setManualItem((prev) => ({
 ...prev,
 status: e.target.value,
 }))
 }
 >
 <option value="Pending">Pending</option>
 <option value="In Progress">In Progress</option>
 <option value="Completed">Completed</option>
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={2}>
 <Button
 variant="warning"
 className="w-100"
 type="button"
 onClick={addManualActivity}
 >
 Add Manual
 </Button>
 </Col>
 </Row>

 {/* ================= TABLE ================= */}

 {form.activitiesCompleted.length > 0 && (
 <div className="table-responsive">
 <Table bordered striped hover size="sm" style={{ minWidth: "1200px" }}>
 <thead>
 <tr>
 <th>BOQ Item</th>
 <th>Activity</th>
 <th>BOQ Qty</th>
 <th>Unit</th>
 <th>Location / Chainage</th>
 <th>Drawing Ref.</th>
 <th>Today Qty</th>
 <th>Prev. Qty</th>
 <th>Total Qty</th>
 <th>Completed</th>
 <th>Wastage</th>
 <th>Status</th>
 <th>Action</th>
 </tr>
 </thead>

 <tbody>
 {form.activitiesCompleted.map((item, i) => (
 <tr key={i}>
 <td>{item.boqName}</td>

 <td>{item.activity}</td>

 <td>{item.boqQty ?? item.quantity ?? "-"}</td>

 <td>{item.unit || "-"}</td>

 <td>{item.location || "-"}</td>

 <td>{item.drawingRef || "-"}</td>

 <td>{item.todayQty ?? item.quantityCompleted}</td>

 <td>{item.previousQtyToDate ?? 0}</td>

 <td>{item.totalQtyToDate ?? item.quantityCompleted}</td>

 <td>{item.quantityCompleted} {item.unit}</td>

 <td className="text-danger fw-bold">
 {item.wastageQty} {item.unit}
 </td>

 <td>{item.status}</td>

 <td>
 <Button
 variant="danger"
 size="sm"
 onClick={() => removeActivityItem(i)}
 >
 Remove
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </div>

 {/* PREVIOUS OPEN HINDRANCES */}
 {form.workOrder && (
 <div className="mb-4">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <Form.Label className="fw-bold mb-0">
 <i className="fas fa-history me-2 text-warning"></i>
 Previous Open Hindrances (Work Order Bottlenecks)
 </Form.Label>
 {loadingPrevIssues && <Spinner size="sm" variant="primary" />}
 </div>

 {previousOpenIssues.length === 0 ? (
 <Card className="border-0 shadow-sm dpr-no-issue-card">
 <Card.Body className="py-2 px-3">
 <small className="text-success fw-semibold">
 <i className="fas fa-check-circle me-1"></i> No unresolved hindrances from previous DPRs for this Work Order.
 </small>
 </Card.Body>
 </Card>
 ) : (
 <Card className="border shadow-sm mb-3 dpr-issue-table-card" style={{ borderLeft: "4px solid #fd7e14" }}>
 <Card.Header className="py-2 bg-light d-flex justify-content-between align-items-center">
 <div>
 <strong>Open Hindrance Resolution Log</strong>
 <small className="text-muted ms-2">
 Review or resolve previous site issues directly in today's DPR.
 </small>
 </div>
 <span className="badge bg-warning text-dark">
 {previousOpenIssues.length} Pending Issue{previousOpenIssues.length > 1 ? "s" : ""}
 </span>
 </Card.Header>
 <Card.Body className="p-0">
 <div className="table-responsive">
 <Table hover bordered size="sm" className="mb-0 align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: "130px" }}>Reported In</th>
 <th>Hindrance / Bottleneck</th>
 <th style={{ width: "100px" }}>Severity</th>
 <th style={{ width: "160px" }}>Status Action</th>
 <th>Action Taken / Resolution Remarks</th>
 </tr>
 </thead>
 <tbody>
 {previousOpenIssues.map((iss) => {
 const key = iss.groupKey || iss.issueId || `${iss.dprId}_${iss.issueIndex}`;
 const currentUpdate = previousIssueUpdates[key] || {
 status: iss.status,
 resolutionRemarks: "",
 };
 const isResolved = currentUpdate.status === "Resolved";

 const dateVal = iss.latestDprDate || iss.dprDate;
 const dprDisplay = iss.dprNumbers?.length > 0 ? iss.dprNumbers.join(", ") : (iss.latestDprNumber || iss.dprNumber || "—");

 return (
 <tr key={key} className={isResolved ? "table-success" : ""}>
 <td style={{ verticalAlign: "top" }}>
 <div className="fw-bold">{dprDisplay}</div>
 {iss.occurrences > 1 && (
 <div className="mt-1">
 <Badge bg="danger" style={{ fontSize: "10px" }}>
 {iss.occurrences}x Reported
 </Badge>
 </div>
 )}
 <small className="text-muted d-block mt-1">
 {dateVal
 ? new Date(dateVal).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 })
 : "—"}
 </small>
 </td>
 <td style={{ verticalAlign: "top" }}>
 {iss.category && (
 <span className="badge bg-light text-secondary border me-2 mb-1" style={{ fontSize: "10px" }}>
 {iss.category}
 </span>
 )}
 <div className="fw-bold">{iss.issue}</div>
 {iss.descriptions?.length > 0 ? (
 iss.descriptions.map((desc, di) => (
 <small key={di} className="text-muted d-block" style={{ fontSize: "11px" }}>
 • {desc}
 </small>
 ))
 ) : iss.description ? (
 <small className="text-muted d-block">{iss.description}</small>
 ) : null}
 {iss.variants?.length > 1 && (
 <small className="text-muted d-block fst-italic mt-1" style={{ fontSize: "10px" }}>
 Consolidates: {iss.variants.join(" | ")}
 </small>
 )}
 <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
 Reported by: {iss.latestReportedBy || iss.reportedBy || "Site Engineer"}
 </small>
 </td>
 <td style={{ verticalAlign: "top" }}>
 <span
 className={`badge bg-${
 iss.severity === "Critical" || iss.severity === "High"
 ? "danger"
 : iss.severity === "Medium"
 ? "warning text-dark"
 : "secondary"
 }`}
 >
 {iss.severity}
 </span>
 </td>
 <td style={{ verticalAlign: "top" }}>
 <Form.Select
 size="sm"
 value={currentUpdate.status}
 onChange={(e) => {
 const newStatus = e.target.value;
 setPreviousIssueUpdates((prev) => ({
 ...prev,
 [key]: {
 ...prev[key],
 status: newStatus,
 },
 }));
 }}
 >
 <option value="Open">Open</option>
 <option value="In Progress">In Progress</option>
 <option value="Resolved">Resolved (Close)</option>
 </Form.Select>
 </td>
 <td style={{ verticalAlign: "top" }}>
 <Form.Control
 size="sm"
 type="text"
 placeholder={
 isResolved
 ? "Required: enter resolution note / action taken..."
 : "Optional remarks / current progress..."
 }
 value={currentUpdate.resolutionRemarks || ""}
 onChange={(e) => {
 const note = e.target.value;
 setPreviousIssueUpdates((prev) => ({
 ...prev,
 [key]: {
 ...prev[key],
 resolutionRemarks: note,
 },
 }));
 }}
 />
 </td>
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 </Card.Body>
 </Card>
 )}
 </div>
 )}

 {/* ISSUES */}
 <div className="mb-4">
 <Form.Label className="fw-bold">Today's New Issues (if any)</Form.Label>

 <Card className="mb-3 border-0 shadow-sm dpr-issue-snapshot-card">
 <Card.Body className="py-3">
 <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
 <div>
 <div className="fw-semibold">Issue Snapshot</div>
 <small className="text-muted">
 Track unresolved items clearly. Critical issues should be visible before submission.
 </small>
 </div>
 <span className="badge bg-dark">{issueStats.total} total</span>
 </div>

 <div className="d-flex flex-wrap gap-2">
 <span className="badge bg-danger">Critical {issueStats.critical}</span>
 <span className="badge bg-warning text-dark">Open {issueStats.open}</span>
 <span className="badge bg-info text-dark">In Progress {issueStats.inProgress}</span>
 <span className="badge bg-success">Resolved {issueStats.resolved}</span>
 </div>

 <div className="mt-2 small text-muted">
 {latestIssue ? (
 <>
 Latest issue: <strong>{latestIssue.issue}</strong> (
 {latestIssue.severity} / {latestIssue.status})
 </>
 ) : (
 "No issues added yet."
 )}
 </div>
 </Card.Body>
 </Card>

 <Row className="g-2 mb-3">
 <Col md={3}>
 <Form.Control
 type="text"
 name="issue"
 value={issueItem.issue}
 placeholder="Issue"
 onChange={handleIssueChange}
 />
 </Col>

 <Col md={4}>
 <Form.Control
 type="text"
 name="description"
 value={issueItem.description}
 placeholder="Description"
 onChange={handleIssueChange}
 />
 </Col>

 <Col md={1}>
 <Form.Select
 className="form-control"
 name="severity"
 value={issueItem.severity}
 onChange={handleIssueChange}
 >
 <option>Low</option>
 <option>Medium</option>
 <option>High</option>
 <option>Critical</option>
 </Form.Select>
 </Col>

 <Col md={2}>
 <Form.Select
 className="form-control"
 name="status"
 value={issueItem.status}
 onChange={handleIssueChange}
 >
 <option>Open</option>
 <option>In Progress</option>
 <option>Resolved</option>
 </Form.Select>
 </Col>

 <Col md={2}>
 <Button type="button" variant="primary" onClick={addIssueItem}>
 Add
 </Button>
 </Col>
 </Row>

 {form.issueDetails.length > 0 && (
 <Table bordered striped size="sm">
 <thead>
 <tr>
 <th>Issue</th>
 <th>Description</th>
 <th>Severity</th>
 <th>Status</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {form.issueDetails.map((item, i) => (
 <tr key={i}>
 <td>{item.issue}</td>
 <td>{item.description || "-"}</td>
 <td>{item.severity}</td>
 <td>{item.status}</td>
 <td>
 <Button
 type="button"
 variant="danger"
 size="sm"
 onClick={() => removeIssueItem(i)}
 >
 Remove
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </div>
 {/* SITE PHOTOS */}
 <div className="mb-4">
 <Form.Label className="fw-bold">Site Photos</Form.Label>

 <Form.Control
 type="file"
 multiple
 accept="image/*"
 onChange={handlePhotoChange}
 className="mb-3"
 />

 {photos.length > 0 && (
 <Row>
 {photos.map((photo, i) => (
 <Col md={3} key={i} className="mb-3">
 <div className="position-relative">
 <img
 src={photo.preview}
 alt="preview"
 style={{
 width: "100%",
 height: "120px",
 objectFit: "cover",
 borderRadius: "6px",
 border: "1px solid #ddd",
 }}
 />
 <button
 type="button"
 className="btn btn-danger btn-xs position-absolute top-0 end-0 m-1"
 style={{ padding: "2px 5px" }}
 onClick={() => removePhoto(i)}
 >
 <i className="fas fa-times"></i>
 </button>
 </div>

 <Form.Control
 className="mt-2"
 placeholder="Photo details..."
 value={photo.description}
 onChange={(e) => {
 const updated = [...photos];
 updated[i].description = e.target.value;
 setPhotos(updated);
 }}
 />
 </Col>
 ))}
 </Row>
 )}
 </div>
 {/* NEXT DAY PLAN */}
 <Form.Group className="mb-4">
 <Form.Label className="fw-semibold">Next Day Plan</Form.Label>
 <div className="dpr-editor-wrapper">
 <ReactQuill
   theme="snow"
   value={form.nextDayPlan || ""}
   onChange={(val) => setForm((prev) => ({ ...prev, nextDayPlan: val }))}
   modules={quillModules}
   placeholder="Enter next day planned activities, manpower/material targets..."
   style={{ minHeight: "120px" }}
 />
 </div>
 </Form.Group>

 {/* WEATHER & WEATHER IMPACT */}
 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Weather</Form.Label>
 <Form.Control
 type="text"
 name="weather"
 placeholder="e.g. Sunny, Rainy, Cloudy"
 value={form.weather}
 onChange={handleChange}
 />
 </Form.Group>
 </Col>
 <Col md={6}>
 <Form.Group>
 <Form.Label>Weather Impacted? (Work Affected)</Form.Label>
 <Form.Select
 className="form-control"
 name="weatherImpact"
 value={form.weatherImpact || "No"}
 onChange={handleChange}
 >
 <option value="No">No</option>
 <option value="Yes">Yes</option>
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 {/* STATUS */}
 <Form.Group className="mb-4">
 <Form.Label>Status</Form.Label>
 <Form.Select
 className="form-control"
 name="status"
 value={form.status}
 onChange={handleChange}
 >
 <option value="Draft">Draft</option>
 <option value="Submitted">Submitted</option>
 </Form.Select>
 </Form.Group>

 {/* SUBMIT */}
 <div className="d-flex gap-2">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? <Spinner size="sm" /> : "Save DPR"}
 </Button>

 <Button
 type="button"
 variant="secondary"
 onClick={() =>
 navigate(
 workOrderFromUrl
 ? `/dpr?workOrder=${workOrderFromUrl}`
 : "/dpr",
 )
 }
 disabled={loading}
 >
 Cancel
 </Button>
 </div>
 </form>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <SignatureModal
 show={showSignatureModal}
 onHide={() => setShowSignatureModal(false)}
 onSave={handleSignatureSave}
 title="Authorised Signatory — Submit DPR"
 />
 </Fragment>
 );
};

export default DprAdd;
